import { useCallback, useMemo, useState } from 'react';
import { Field, Validations } from '..';
import './index.css';

interface Props {
  config: Field;
  width?: number | string;
}

const InputField = (props: Props) => {
  const { config, width } = props;
  const {
    label,
    value,
    error,
    handleValueChange,
    isLocked,
    title,
    isSecret,
    shouldFit,
    handleEnterPress,
    fieldMaxSize,
    isDisabled,
  } = config;

  const [isFocussed, setIsFocussed] = useState(false);

  const handleChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      handleValueChange(value ?? '');
    },
    [handleValueChange],
  );

  const fieldClassName = `field ${
    (isLocked ? isFocussed : isFocussed || typeof value !== 'undefined')
      ? 'focussed'
      : ''
  } ${isLocked && !isFocussed ? 'locked' : ''} ${shouldFit ? 'fit' : ''}`;

  const errorTranslations = useMemo(() => {
    switch (error) {
      case Validations.NonZero:
        return 'Balance must be greater than 0';

      case Validations.Required:
        return 'This field is required';

      case Validations.NumberTyping:
        return 'This value must be a number';

      case Validations.BooleanTyping:
        return 'This value must be a boolean';

      case Validations.BalanceTypeTyping:
        return 'This value must be either stable or asset';

      case Validations.BalanceTypeTyping:
        return 'This value must be a date';

      case Validations.DirectionType:
        return 'This value must be either up or down';

      case Validations.MrcStateType:
        return 'This value must either INITIALISED / PENDING / RESET';

      case Validations.OrderType:
        return 'This value must be either BUY or SELL';

      case Validations.MaximumExceeded:
        return 'Maximum value exceeded';

      case Validations.MinimumRequired:
        return 'Minimum balance required';

      case Validations.Minimum50Required:
        return 'Minimum of $50 required';

      case Validations.ConnectionRefused:
        return 'Connection Refused';

      case Validations.InvalidEmail:
        return 'Invalid email address';

      case Validations.OutOfSyncPassword:
        return 'Password is not the same';

      default:
        return 'Unknown error...';
    }
  }, [error]);

  const handleKeyDown = useCallback(
    async (e: any) => {
      if (e.key === 'Enter') {
        handleEnterPress?.();
      }
    },
    [handleEnterPress],
  );

  const labelSize = useMemo(() => {
    if (!fieldMaxSize) {
      return 10;
    }

    return Math.min(10, fieldMaxSize / 10);
  }, [fieldMaxSize]);

  return (
    <>
      {!!title && <p className="field-title">{title}</p>}

      <div
        className={fieldClassName}
        style={{ maxWidth: fieldMaxSize, paddingRight: 28, width }}
      >
        <input
          id={label}
          type={isSecret ? 'password' : 'type'}
          value={value}
          placeholder={label}
          onChange={handleChange}
          onFocus={() => !isLocked && setIsFocussed(true)}
          onBlur={() => !isLocked && setIsFocussed(false)}
          onKeyDown={handleKeyDown}
          style={{ width }}
          disabled={isDisabled}
        />

        <label
          htmlFor={label}
          className={error && 'error'}
          style={{
            fontSize: labelSize,
            lineHeight: 1,
            paddingTop: 5,
            paddingRight: 5,
          }}
        >
          {error ? errorTranslations : label}
        </label>
      </div>
    </>
  );
};

export default InputField;
